import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useAuth } from "@/contexts/AuthContext";
import { logEvent } from "@/hooks/use-event-log";

const AUTOSEND_KEY = "recurring_autosend_enabled";

/**
 * VIB-1293: global auto-send-bryter for fakturamotoren. Manglende/ikke-true
 * verdi = av (fail-safe). Lest av run-recurring-invoices; motoren sender kun
 * når BÅDE denne OG avtalens auto_send er true.
 */
export function useRecurringAutosend() {
  return useQuery({
    queryKey: ["system-config", AUTOSEND_KEY],
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase
        .from("system_config")
        .select("value")
        .eq("key", AUTOSEND_KEY)
        .maybeSingle();
      if (error) throw error;
      return data?.value === true;
    },
    staleTime: 60 * 1000,
  });
}

/**
 * Slår bryteren av/på. Dette er en SIKKERHETS-/ØKONOMI-event: aktør er ALLTID
 * den faktisk innloggede brukeren (useAuth), ALDRI en impersonert bruker
 * (audit-regel 1). Egen event_type config.autosend_toggled.
 */
export function useToggleRecurringAutosend() {
  const queryClient = useQueryClient();
  const { user: authUser } = useAuth();

  return useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await supabase
        .from("system_config")
        .upsert(
          {
            key: AUTOSEND_KEY,
            value: enabled,
            updated_by: authUser?.id ?? null,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "key" },
        );
      if (error) throw error;
    },
    onSuccess: async (_d, enabled) => {
      await invalidateFor(queryClient, "system_config", { key: AUTOSEND_KEY });
      logEvent({
        event_type: "config.autosend_toggled",
        entity_type: "system_config",
        entity_id: AUTOSEND_KEY,
        user_id: authUser?.id, // ekte innlogget bruker, aldri impersonert
        severity: "warning",
        payload: { key: AUTOSEND_KEY, enabled },
      });
      toast.success(
        enabled
          ? "Automatisk utsending PÅ — avtaler med auto-send sendes automatisk"
          : "Automatisk utsending AV — fakturaer lages kun som utkast",
      );
    },
    onError: (err: Error) => toast.error(`Kunne ikke endre bryteren: ${err.message}`),
  });
}
